import { displayErrorToast } from "@endya-conformite/ui-kit";
import {
  ConformiteDeclarationActiviteIobspForm,
  useDeclarationActiviteIobspForm,
} from "@src/conformite/declarationActivite/edit/ConformiteDeclarationActiviteIobspForm";
import { useMillesime } from "@src/store/store.millesime";
import { useCallback, useEffect, useState } from "react";
import { GatewayDeclarationActivite } from "@conformite/gateway";
import { GatewayDeclarationActiviteApi } from "@src/api/conformite/declarationActivite.api";
import { handleGatewayError } from "@src/api/utils/handleGatewayError";
import { useEntreprises } from "@src/store/store.entreprise";
import {
  ParcoursConformiteStepComponentProps,
  useParcoursConformite,
} from "../../ParcoursConformite";
import { ParcoursConformiteTemplate } from "../../ParcoursConformiteTemplate";
import { ParcoursConformiteLoaderStep } from "../../ParcoursConformiteLoaderStep";

type FormContainerProps = {
  siren: string;
  millesime: number;
  declarationActivite: GatewayDeclarationActivite.DeclarationActiviteIobsp | null;
} & ParcoursConformiteStepComponentProps;

function FormContainer({
  declarationActivite,
  millesime,
  siren,
  changeStep,
  currentStep,
}: FormContainerProps) {
  const { entreprise } = useParcoursConformite();
  const { updateAvancement } = useEntreprises();
  const { form, onSubmit } = useDeclarationActiviteIobspForm(
    siren,
    millesime,
    declarationActivite,
    (_, avancement) => {
      updateAvancement(avancement);
    }
  );

  const handleSubmit = useCallback(async () => {
    if (form.formState.isDirty) await onSubmit(form.getValues());
  }, [onSubmit, form]);

  return (
    <ParcoursConformiteTemplate
      currentStep={currentStep}
      changeStep={changeStep}
      isConforme={entreprise?.avancement.declarationActiviteIobsp === 100}
      isDirty={form.formState.isDirty}
      size="large"
      title="Déclaration d'activités IOBSP"
      onSubmit={handleSubmit}
    >
      <ConformiteDeclarationActiviteIobspForm form={form} />
    </ParcoursConformiteTemplate>
  );
}

export function ConformiteDeclarationActiviteIobspStep({
  siren,
  changeStep,
  currentStep,
  ...props
}: ParcoursConformiteStepComponentProps) {
  const { selected: millesime } = useMillesime();
  const [declarationActivite, setDeclarationActivite] =
    useState<GatewayDeclarationActivite.DeclarationActiviteIobsp | null>();
  useEffect(() => {
    if (!siren || !millesime) return;
    GatewayDeclarationActiviteApi.getIobsp(siren, millesime)
      .then((data) => {
        setDeclarationActivite(data);
      })
      .catch((error) => {
        handleGatewayError({
          onUnhandled: () => {
            displayErrorToast(
              "Une erreur est survenue lors de la récupération du formulaire"
            );
          },
        })(error);
      });
  }, [siren, millesime]);

  if (!millesime || declarationActivite === undefined)
    return (
      <ParcoursConformiteLoaderStep
        currentStep={currentStep}
        changeStep={changeStep}
        title="Déclaration d'activités IOBSP"
        size="large"
      />
    );
  return (
    <FormContainer
      currentStep={currentStep}
      changeStep={changeStep}
      siren={siren}
      millesime={millesime}
      declarationActivite={declarationActivite}
      {...props}
    />
  );
}
